<template>
  <component
    :is="page ? 'q-page': 'q-dialog'"
    v-model="shown"
    maximized
    @hide="hide"
  >
    <q-card
      :style="{
        'height': !page && $q.screen.gt.sm ? '96%' : '100%',
        'max-height': !page && $q.screen.gt.sm ? '96%' : '100%',
        'min-height': page ? 'inherit' : '',
        'width': !page && $q.screen.gt.sm ? '96%' : '100%',
        'max-width': !page && $q.screen.gt.sm ? '96%' : '100%'
      }"
      class="column"
    >
      <q-card-section
        ref="seatChartContainer"
        style="width: 100%;"
        class="no-padding relative-position"
        :class="isCustomerFacingDisplay ? 'col row q-gutter-none' : 'col column items-center'"
      >
        <template v-if=" showPinchZoomAnimation">
          <pinch-zoom-animation
            v-if="$q.platform.has.touch"
          />
          <scroll-zoom-animation
            v-else
          />
        </template>
        <legend-seats
          ref="seatChartLegend"
          class="bg-transparent col-auto"
          :class="isCustomerFacingDisplay ? 'column q-pa-md' : 'col-auto q-px-md q-pt-sm'"
          :style="{ width: isCustomerFacingDisplay ? '100px' : '100%' }"
          :seat-chart-configuration="seatChartForShowing.seatChartConfiguration"
          :initial-selected-seats="initialSelectedSeats"
          :seat-chart="seatChartForShowing"
          :seat-preferences="seatPreferences"
          :show-full-legend="$q.screen.gt.sm"
          :vertical="isCustomerFacingDisplay"
          @hover-seat="seat => hoverSeat(seat)"
        />

        <pinch-scroll-zoom
          ref="zoomer"
          class="bg-transparent col column items-center overflow-hidden relative-position"
          key-actions
          key-controls
          :within="false"
          :width="zoomerWidth"
          :height="zoomerHeight"
          :min-scale="initialMinScale"
          :max-scale="maxScale"
          @scaling="e => onEvent('scaling', e)"
          @stop-drag="e => onEvent('stopDrag', e)"
        >
          <div
            v-if="isCustomerFacingDisplay"
            class="col-auto full-width q-mb-xs"
          >
            <div class="screen q-mx-auto" />
            <div
              class="text-white text-h6 q-px-sm text-bold text-center text-uppercase"
              style="line-height: 1;"
            >
              {{ $tc('screen.screen', 1) }}
            </div>
          </div>

          <seat-chart
            v-if="!seatChartForShowing.loading"
            ref="seatChart"
            :screen-name="!isCustomerFacingDisplay ? showing.screen.name : undefined"
            :heat-map-mode="heatMapMode"
            :seat-chart="seatChartForShowing"
            :seat-chart-configuration="seatChartForShowing.seatChartConfiguration"
            :show-seat-numbers="currentSite.posShowReservedSeatLabels"
            :hide-unavailable-seat-numbers="true"
            :seats-to-select="seatsToSelect"
            :has-marathon="showing && showing.hasMarathon"
            :ticket-type-id="ticketTypeId"
            :initial-selected-seats="initialSelectedSeats"
            :seat-preferences="seatPreferences"
            :show-screen="!isCustomerFacingDisplay"
            :show-footer="false"
            :show-header="false"
            :active-seat="activeSeat"
            is-full-screen
            :container-width="containerWidth"
            @update:model-value="(input) => handleSeatChartInput(input)"
          />

          <q-inner-loading :showing="seatChartForShowing.loading" />
        </pinch-scroll-zoom>
        <q-card-actions
          class="justify-between q-pa-sm"
          :class="{
            'full-width row': !isCustomerFacingDisplay,
            'col-1 column items-end absolute-right': isCustomerFacingDisplay
          }"
        >
          <div
            :class="{ 'column text-center': isCustomerFacingDisplay }"
            :style="isCustomerFacingDisplay ? '' : 'display: flex; align-items: center;'"
          >
            <template v-if="isCustomerFacingDisplay && selectedSeats && selectedSeats.length > 0">
              <q-chip
                v-for="seat in selectedSeats"
                :key="seat"
                class="text-h6"
                dense
                square
              >
                {{ seat }}
              </q-chip>
            </template>
            <div
              v-else
              :class="{ 'text-h5': !isCustomerFacingDisplay }"
            >
              {{ selectionDescription }}
            </div>
          </div>
          <div
            v-if="!viewOnly && seatPreferences.y_ratio_strength"
            class="row col-auto items-center"
          >
            <q-chip
              class="col"
              :icon="seatPreferences.customizing ? undefined : 'manage_accounts'"
              color="accent"
              text-color="white"
              :label="positionDescription"
            />
            <q-slider
              v-model="seatPreferences.y_ratio"
              class="col-auto q-mx-md"
              vertical
              color="grey"
              track-color="grey"
              style="height: 35px;"
              :min="0"
              :max="1"
              :step="0.01"
              @update:model-value="seatPreferences.customizing = true"
            />
          </div>
          <q-chip
            v-if="!viewOnly && seatPreferences.y_ratio_strength"
            icon="manage_accounts"
            color="positive"
            text-color="white"
            :label="seatDescription"
          />
          <q-btn
            v-if="!viewOnly && $refs.seatChart && $refs.seatChart.previousSeatNames && $refs.seatChart.previousSeatNames.length"
            icon="manage_accounts"
            color="blue"
            size="sm"
            no-wrap
            outline
            @click="$refs.seatChart.selectPriorSeats()"
          >
            <div
              style="max-width: 125px;"
              class="ellipsis"
            >
              {{ 'Prior Seats - ' + $refs.seatChart.previousSeats.movie_name }}
              >
            </div>
          </q-btn>
          <q-btn
            v-if="!viewOnly && seatPreferences.y_ratio_strength"
            :label="heatMapLabel"
            outline
            size="sm"
            @click="toggleHeatMap"
          />
          <q-btn-group
            class="q-gutter-sm col-auto"
            :class="{ 'column': isCustomerFacingDisplay }"
            flat
            square
          >
            <q-btn
              class="border-responsive-grey-1 q-pa-xs"
              color="responsive"
              dense
              text-color="responsive"
              icon="fas fa-minus"
              :disabled="initialScale >= currentScale"
              @click="onZoomOut()"
            />
            <q-btn
              class="border-responsive-grey-1 q-px-xs"
              color="responsive"
              dense
              text-color="responsive"
              icon="fas fa-plus"
              :disabled="currentScale >= maxScale"
              @click="onZoomIn()"
            />
            <q-btn
              v-if="!viewOnly"
              :label="$t('actions.reserve')"
              color="primary"
              :text-color="$isColorDark(currentSite.primaryColor) ? 'white' : 'black'"
              :disable="!canSave"
              :loading="saving"
              data-test-id="seats-dialog-reserve-button"
              @click="assignSeatsToTickets"
            />
            <q-btn
              v-if="!page"
              v-close-popup
              :label="$t('actions.close')"
              color="secondary"
              :text-color="$isColorDark(currentSite.secondaryColor) ? 'white' : 'black'"
            />
          </q-btn-group>
        </q-card-actions>
      </q-card-section>
    </q-card>
  </component>
</template>

<script>
import { Cookies } from 'quasar'
import LegendSeats from 'src/modules/admin/seat-chart/LegendSeats'
import PinchZoomAnimation from 'src/modules/admin/seat-chart/PinchZoomAnimation'
import ScrollZoomAnimation from 'src/modules/admin/seat-chart/ScrollZoomAnimation'
import SeatChart from 'src/modules/admin/seat-chart/SeatChart'
import SeatChartMixin from 'src/modules/admin/seat-chart/SeatChartMixin'

const sortAlphaNum = (a, b) => a.localeCompare(b, 'en', { numeric: true })

export default {
  name: 'SeatsDialog',
  components: {
    SeatChart,
    LegendSeats,
    PinchZoomAnimation,
    ScrollZoomAnimation
  },
  mixins: [SeatChartMixin],
  props: {
    page: {
      type: Boolean,
      required: false,
      default: false
    },
    viewOnly: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  models: {
    seatChartForShowing: {
      variables () {
        return {
          showingId: this.showing?.id,
          orderId: this.order?.isRemote ? this.order.id : null,
          skip: !this.showing
        }
      },
      immediate: true,
      force: true
    }
  },
  emits: ['done'],
  data () {
    return {
      shown: false,

      saving: false,

      selectedSeats: [],
      selectionDescription: null,
      selectionComplete: false,

      order: null,
      showing: null,
      ticket: null,

      heatMapMode: 'none',

      showSeatChart: false,

      isCustomerFacingDisplay: !this.$customerDisplayHandler?.isRegister,
      zoomerWidth: null,
      zoomerHeight: null,
      containerWidth: null,
      showPinchZoomAnimation: false,
      showFullScreen: this.$q.screen.gt.xs
    }
  },
  computed: {
    ticketTypeId () {
      if (!this.ticket) {
        return false // This will show all seats
      } else if (this.ticket.ticketType.appliesToSpecificSeats) {
        return this.ticket.ticketType.id
      } else {
        return null // This will only show seats that aren't set to a specific ticket type
      }
    },
    ticketTypeIds () {
      if (this.ticketTypeId === false) return false

      if (!this.ticketTypeId || !this.seatChartForShowing) return null

      // The the smallest subset of ticket type ids all sets of seats with this id has
      // Do this because this is the unit we are selecting over
      const matchingTicketTypeIds = _(this.seatChartForShowing.seatChart)
        .flatten()
        .filter(seat => seat.limitTicketTypes && _.includes(seat.ticketTypeIds, this.ticketTypeId))
        .sort(seat => seat.ticketTypeIds.length)
        .value()

      return matchingTicketTypeIds.length ? matchingTicketTypeIds[0].ticketTypeIds : null
    },
    initialSelectedSeats () { return this.order && this.order.currentSelectedSeats(this.showing, this.ticketTypeIds) },
    seatsToSelect () { return this.order && this.order.countSeatsToSelect(this.showing, this.ticketTypeIds) },
    canSave () { return this.selectionComplete && !_.isEqual(this.initialSelectedSeats, this.selectedSeats) },
    seatPreferences () {
      return this.order?.user?.seatPreferences || { y_ratio: 0.5 }
    },
    seatDescription () {
      if (!this.seatPreferences.seat_type_preference) return

      const topSeatType = _.maxBy(_.keys(this.seatPreferences.seat_type_preference), seatType => this.seatPreferences.seat_type_preference[seatType])

      const description = _.startCase(topSeatType)
      const weight = this.seatPreferences.seat_type_preference[topSeatType]

      if (weight < 1) {
        return `Mild ${description} Preference`
      } else if (weight > 1.9) {
        return `Strong ${description} Preference`
      } else {
        return `${description} Preference`
      }
    },
    positionDescription () {
      let description = ''

      if (!this.seatPreferences.customizing && this.seatPreferences.y_ratio_strength && this.seatPreferences.y_ratio_strength > 1.75) description += 'Strong '

      if (this.seatPreferences.y_ratio === 0) description += 'Front Row'
      else if (this.seatPreferences.y_ratio === 1) description += 'Back Row'
      else if (this.seatPreferences.y_ratio < 0.35) description += 'Towards Front'
      else if (this.seatPreferences.y_ratio > 0.75) description += 'Towards Back'
      else description += 'Middle'

      if (!this.seatPreferences.customizing && this.seatPreferences.y_ratio_strength > 1) description += ' Preference'

      return description
    },
    heatMapLabel () {
      if (this.heatMapMode === 'count') {
        return 'Viewing Count'
      } else if (this.heatMapMode === 'preference') {
        return 'Viewing Preference'
      } else {
        return 'Heat Map'
      }
    }
  },
  watch: {
    'seatChartForShowing.loading': {
      handler () {
        // Finished fetching data
        const viewPortZoom = this.isCustomerFacingDisplay ? 1 : document.body.style.zoom
        const legendAndInfoBarHeight = this.isCustomerFacingDisplay ? 0 : 240 / viewPortZoom
        const legendAndInfoBarWidth = this.isCustomerFacingDisplay ? 100 : 0
        const viewportRatio = this.isCustomerFacingDisplay ? 1 : 0.96

        this.zoomerWidth = window.innerWidth / viewPortZoom * viewportRatio - legendAndInfoBarWidth
        this.zoomerHeight = window.innerHeight / viewPortZoom * viewportRatio - legendAndInfoBarHeight
        if (this.seatChartForShowing.loaded && !this.seatChartForShowing.loading) {
          this.handleSeatChartDataFetched()

          const seatChartCookies = Cookies.get(this.$constants.cookies.seatChartCookies)
          const seatChartCookiesSiteId = seatChartCookies?.siteId
          const hasSeatChartCookies = !!seatChartCookies && seatChartCookiesSiteId === this.currentSite.id
          if (!hasSeatChartCookies) {
            if (this.seatChartForShowing.loading) return
            this.showPinchZoomAnimation = true
            setTimeout(() => {
              this.showPinchZoomAnimation = false
              Cookies.set(this.$constants.cookies.seatChartCookies, { siteId: this.currentSite.id, showPinchZoomAnimation: true, type: this.$constants.cookies.type.consumerConsent, expires: 1 })
            }, 4000)
          }
        }
      },
      immediate: true
    }
  },
  created () {
    this.selectedSeats = _.clone(this.initialSelectedSeats)
  },
  configuration: ['currentSite'],
  methods: {
    handleSeatChartInput (input) {
      this.selectedSeats = input.selectedSeats
      this.selectionComplete = input.complete
      this.selectionDescription = input.description
    },
    async show (order, showing, ticket) {
      this.order = order

      if (!showing) {
        showing = order.firstOrderItemNeedingSeatSelection()?.ticket?.showing
      }

      this.showing = showing

      if (ticket) {
        this.ticket = ticket

        const numSelectedSeats = this.order.currentSelectedSeats(this.showing, this.ticketTypeIds).length
        const numTickets = this.order.showingTicketOrderItems(this.showing, this.ticketTypeIds).length

        // If auto select is enabled and only some of the tickets have seats
        // selected, clear the partial selections so that autoselect puts all
        // ticket together
        if (this.currentSite.autoSelectSeats && numSelectedSeats > 0 && numSelectedSeats !== numTickets) {
          this.order.clearSeatSelection(this.showing, this.ticketTypeIds)
        }
      } else {
        // Auto select the type of seat to select from the list remaining
        const remainingOrderItemForSeatSelection = _.find(this.order.showingTicketOrderItems(this.showing), orderItem => !orderItem?.ticket?.seatName)
        if (!remainingOrderItemForSeatSelection) {
          return
        }
        this.ticket = remainingOrderItemForSeatSelection.ticket
      }

      if (this.$customerDisplayHandler) this.$customerDisplayHandler.publish('seats', [this.order, this.showing, this.ticket])

      // Show and wait for fetch
      // Always force fetch so we know this is current at the time of render
      this.shown = true
      await this.$nextTick()
      await this.seatChartForShowing.fetch(true)
    },
    async handleSeatChartDataFetched () {
      await this.$nextTick()

      // Run auto select routine
      if (this.currentSite.autoSelectSeats && this.$refs.seatChart) {
        this.$refs.seatChart.autoSelect()
      }

      const mostRowOrColumnCount = Math.max(this.seatChartForShowing.seatChart.length, this.seatChartForShowing.seatChart[0].length)
      this.autoZoom = Math.round(mostRowOrColumnCount / 12)
      this.maxScale = this.$q.screen.gt.xs ? Math.round(mostRowOrColumnCount / 20) : this.autoZoom + 0.5
      // Change the width of the chart so the total height fits into the view
      this.resizeChart()
    },
    hide () {
      this.shown = false
      if (this.$customerDisplayHandler) this.$customerDisplayHandler.publish('seats', [], false, false)
    },
    async resizeChart () {
      await this.$nextTick()
      const seatChart = this.$refs.seatChart

      if (!seatChart) return

      const heightWithControls = this.zoomerHeight
      const widthWithControl = this.zoomerWidth

      const seatChartHeight = seatChart.$refs['seat-chart'].clientHeight
      const seatChartWidth = seatChart.$refs['seat-chart'].clientWidth
      const heightRatio = heightWithControls / seatChartHeight
      const widthRatio = widthWithControl / seatChartWidth

      if (heightRatio < 1) {
        this.initialScale = this.isCustomerFacingDisplay ? heightRatio - 0.05 : heightRatio
        this.originX = widthWithControl / 2
        this.originY = this.isCustomerFacingDisplay ? heightWithControls * 0.05 : 0
      } else if (widthRatio < 1) {
        this.initialScale = widthRatio
        this.originX = 0
        this.originY = heightWithControls / 2
      } else {
        this.initialScale = 1
        this.originX = 0
        this.originY = 0
      }

      this.currentScale = this.initialScale
      this.containerWidth = this.zoomerWidth
      this.initialMinScale = Math.max(this.initialScale - 0.3, 0.1)
      this.adjustZoom(this.initialScale)
    },

    async assignSeatsToTickets () {
      if (!this.selectionComplete) return

      this.order.setSeatSelection(this.showing, [...this.selectedSeats].sort(sortAlphaNum), this.ticketTypeIds)

      const remainingOrderItemForSeatSelection = _.find(this.order.showingTicketOrderItems(this.showing), orderItem => !orderItem?.ticket?.seatName)
      if (remainingOrderItemForSeatSelection) {
        await this.seatChartForShowing.fetch(true)

        this.ticket = remainingOrderItemForSeatSelection.ticket
        this.selectedSeats = []
        this.$refs.seatChart?.clearSelection()
        await this.$nextTick()
        if (this.currentSite.autoSelectSeats) {
          this.$refs.seatChart?.autoSelect()
        }

        this.resizeChart()
        return
      }

      this.saving = true
      if (this.order.errorsByShowing[this.showing.id]) {
        this.saving = false
        this.$mh.showErrors(this.order.errorsByShowing[this.showing.id])
        this.$rollbar.warning(`Order failed to set seat selection. Errors: ${this.order.errorsByShowing[this.showing.id].join(', ')}`)
        return
      }
      const { errors } = await this.order.save()
      this.saving = false

      if (errors && errors.length) {
        this.$mh.showErrors(errors)
        this.order.clearSeatSelection(this.showing)
        this.selectedSeats = []
        this.$refs.seatChart?.clearSelection()
        await this.seatChartForShowing.fetch(true)
        return
      }

      this.shown = false
      this.$emit('done')
    },
    toggleHeatMap () {
      if (this.heatMapMode === 'count') {
        this.heatMapMode = 'preference'
      } else if (this.heatMapMode === 'preference') {
        this.heatMapMode = null
      } else {
        this.heatMapMode = 'count'
      }
    }
  }
}

</script>

<style lang="scss" scoped>
  .seat-chart-wrapper, .control-bar {
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    background-color: rgba(33,33,33,.3);
    padding: 15px;
    overflow: auto;
  }

  .control-bar {
    background-color: rgba(66,66,66,.6);
    z-index: 1;
  }

  .screen-wrapper {
    width: 100%;
    color: #EEE;
    transition: width 0.5s;
  }
  .seat-chart {
    width: 100%;
    transition: width 0.5s;
  }
  .seat-chart.zoom, .screen-wrapper.zoom {
    width: 200%;
  }
  .seat-chart-row {
    width: auto;
  }
  .seat {
    flex-grow: 1;
    flex-shrink: 1;
    width: 20px;
  }
  .seat-content {
    width: 100%;
    position: relative;
  }
  svg {
    width: 100%;
    height: auto;
  }
  .seat-text {
    font-weight: 800;
    color: white;
    text-align: center;
    position: absolute;
    top: 15%;
    left: 0;
    right: 0;
    bottom: 0;
    user-select: none;
  }
  .seat-content {
    padding: 20% 5%;
  }
  .seat-content.regular-seat {
    padding: 20% 10%;
  }

  .seat-content.couch-left, .seat-content.couch-corner-left {
    padding: 20% 0% 20% 10%;
  }

  .seat-content.couch-vertical-east-left {
    padding: 40% 10% 0% 10%;
  }

  .seat-content.couch-right, .seat-content.couch-corner-right {
    padding: 20% 10% 20% 0%;
  }

  .seat-content.couch-vertical-west-right {
    padding: 40% 10% 0% 10%;
  }

  .seat-content.couch-center {
    padding: 20% 0%;
  }

  .seat-content.couch-vertical-east-center {
    margin-top: -10%;
    margin-bottom: -20%;
    padding: 0%;
  }

  .seat-content.couch-vertical-west-center {
    margin-top: -10%;
    margin-bottom: -20%;
    padding: 0%;
  }

  .seat-content.table, .seat-content.table-left, .seat-content.table-right, .seat-content.table-center {
    padding: 30% 0 0 0;
  }

  .seat-content.table-left {
    padding: 30% 0 0 0;
  }

  .seat-content.table-right {
    padding: 30% 0 0 0;
  }

  .seat-content.table-center {
    padding: 30% 0 0 0;
  }

  .social-distancing {
    opacity: 0.4
  }

  .previous-selection {
    border: dashed blue 2px;
    margin: -2px 0px -2px -2px;
    width: 24px;
  }

  .top-legend .previous-selection {
    width: 29px;
    height: 35px;
  }

  .legend .previous-selection {
    width: 38px;
    height: 44px;
  }

  .top-legend-seat-name {
    font-size: 1.5vh;
    font-weight: 500;
  }
  .seat {
    color: var(--q-seatAvailable);
  }
  .reserved, .unavailable:not(.selected) {
    color: #AAA;
  }
  .selected {
    color: var(--q-seatSelected);
  }
  .selected.aisle {
    background-color: var(--q-seatSelected);
  }
  .screen {
    border-top: 24px solid #AAA;
    border-left: 40px solid transparent;
    border-right: 40px solid transparent;
    height: 0px;
    width: 100%;
    max-width: 934.4px;
    margin-bottom: -21px;
  }

</style>
